import React from 'react';

import type { StatusIndicatorProps } from './StatusIndicator.types';
import { ThinkingDots } from './ThinkingDots';
import { TypewriterCursor } from './TypewriterCursor';

/**
 * Displays a visual indicator for assistant status (thinking or streaming)
 * Automatically selects the appropriate variant based on status if not specified
 *
 * @example
 * ```tsx
 * <StatusIndicator status="thinking" />
 * <StatusIndicator status="streaming" size="lg" />
 * <StatusIndicator status="idle" /> // Returns null
 * ```
 */
export const StatusIndicator: React.FC<StatusIndicatorProps> = ({
  status,
  variant,
  size = 'md',
  color,
  className,
}) => {
  // If idle, don't render anything
  if (status === 'idle') {
    return null;
  }

  // Determine variant based on status if not explicitly provided
  const effectiveVariant = variant || (status === 'thinking' ? 'dots' : 'typewriter');

  // Render the appropriate indicator
  if (effectiveVariant === 'typewriter') {
    return <TypewriterCursor size={size} color={color} />;
  }

  // Default to thinking dots (includes 'dots' and 'pulse' variants)
  return <ThinkingDots size={size} color={color} />;
};
